"use client";

import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Header from "@/src/features/landingPage/Header";
import Footer from "@/src/features/landingPage/Footer";

const PolicySection = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(16),
  paddingBottom: theme.spacing(8),
  backgroundColor: (theme.vars || theme).palette.background.default,
  minHeight: "100vh",
  transition: "background-color .3s ease",
}));

const PolicyPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  maxWidth: "800px",
  margin: "0 auto",
  borderRadius: theme.spacing(2),
  boxShadow:
    (theme.vars || theme).shadows?.[1] || "0 8px 40px rgba(0,0,0,0.12)",
  backgroundColor: (theme.vars || theme).palette.background.paper,
  transition: "background-color .3s ease, box-shadow .3s ease",
}));

const StyledTable = styled("table")(({ theme }) => ({
  width: "100%",
  borderCollapse: "collapse",
  marginBottom: theme.spacing(2),
  fontSize: "0.95rem",
  "& thead th": {
    backgroundColor: (theme.vars || theme).palette.action.hover,
  },
  "& th, & td": {
    border: `1px solid ${(theme.vars || theme).palette.divider}`,
    padding: theme.spacing(1.5),
    textAlign: "left",
    verticalAlign: "top",
  },
  "& th": {
    fontWeight: 600,
    color: (theme.vars || theme).palette.text.primary,
  },
  "& td": {
    color: (theme.vars || theme).palette.text.primary,
  },
}));

const PrivacyPolicy: React.FC = () => {
  return (
    <>
      <Header />
      <PolicySection>
        <Container maxWidth="md">
          <PolicyPaper elevation={0}>
            <Stack spacing={4}>
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography
                  variant="h1"
                  component="h1"
                  sx={(theme) => ({
                    fontSize: { xs: "2rem", md: "2.5rem" },
                    fontWeight: 600,
                    color: (theme.vars || theme).palette.text.primary,
                    mb: 2,
                  })}
                >
                  Privacy Policy
                </Typography>
                <Typography
                  variant="body1"
                  sx={(theme) => ({
                    color: (theme.vars || theme).palette.text.secondary,
                    fontSize: "1rem",
                  })}
                >
                  Last updated: June 5, 2025
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography
                  variant="h3"
                  sx={(theme) => ({
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: (theme.vars || theme).palette.text.primary,
                    mb: 2,
                  })}
                >
                  1. Who We Are
                </Typography>
                <Typography
                  variant="body1"
                  sx={(theme) => ({
                    color: (theme.vars || theme).palette.text.primary,
                    lineHeight: 1.6,
                    mb: 3,
                  })}
                >
                  Bouncepoint, Inc. (&quot;<strong>Company,</strong>&quot;
                  &quot;
                  <strong>we,</strong>&quot; &quot;<strong>us</strong>&quot; or
                  &quot;
                  <strong>our</strong>&quot;) is a Delaware corporation with its
                  principal place of business in Tacoma, Washington. We operate
                  the website located at <strong>Bouncepoint.io</strong> (the
                  &quot;
                  <strong>Site</strong>&quot;). This Privacy Policy describes
                  how we collect, use, disclose, and safeguard information when
                  you visit the Site or otherwise interact with us.
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="h3"
                  sx={(theme) => ({
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: (theme.vars || theme).palette.text.primary,
                    mb: 2,
                  })}
                >
                  2. Information We Collect
                </Typography>
                <Box sx={{ mb: 3, overflowX: "auto" }}>
                  <StyledTable>
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Examples</th>
                        <th>Purpose</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Information You Provide</td>
                        <td>
                          • Email address submitted via our &quot;I&apos;m
                          Interested&quot; form
                          <br />• Any content you voluntarily send us (e.g.,
                          questions, feedback)
                        </td>
                        <td>
                          • Respond to your inquiries
                          <br />• Send product updates, newsletters, and
                          marketing communications
                        </td>
                      </tr>
                      <tr>
                        <td>Automatic Data</td>
                        <td>
                          • IP address, browser type & version, device
                          identifiers
                          <br />• Pages viewed, referring URLs, time/date of
                          visits
                          <br />• Cookies, pixels, and similar tracking
                          technologies
                        </td>
                        <td>
                          • Site analytics and performance
                          <br />• Security & fraud prevention
                          <br />• Improving Site functionality and marketing
                          effectiveness
                        </td>
                      </tr>
                    </tbody>
                  </StyledTable>
                </Box>
                <Typography
                  variant="body1"
                  sx={(theme) => ({
                    color: (theme.vars || theme).palette.text.primary,
                    lineHeight: 1.6,
                    mb: 3,
                  })}
                >
                  <strong>Cookies & Tracking</strong> – We (or third-party
                  analytics providers) place cookies or similar technologies on
                  your device. You may disable cookies through your browser
                  settings, but the Site may not function optimally.
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="h3"
                  sx={(theme) => ({
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: (theme.vars || theme).palette.text.primary,
                    mb: 2,
                  })}
                >
                  3. How We Use Your Information
                </Typography>
                <Typography
                  variant="body1"
                  sx={(theme) => ({
                    color: (theme.vars || theme).palette.text.primary,
                    lineHeight: 1.6,
                    mb: 2,
                  })}
                >
                  We use personal information to:
                </Typography>
                <Box sx={{ ml: 2, mb: 3 }}>
                  <Typography
                    variant="body1"
                    sx={(theme) => ({
                      color: (theme.vars || theme).palette.text.primary,
                      mb: 1,
                    })}
                  >
                    Provide, maintain, and improve the Site and any future
                    services;
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={(theme) => ({
                      color: (theme.vars || theme).palette.text.primary,
                      mb: 1,
                    })}
                  >
                    Communicate with you about product updates, newsletters, or
                    marketing offers (you may opt out at any time);
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={(theme) => ({
                      color: (theme.vars || theme).palette.text.primary,
                      mb: 1,
                    })}
                  >
                    Monitor and analyze Site usage and trends;
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={(theme) => ({
                      color: (theme.vars || theme).palette.text.primary,
                      mb: 1,
                    })}
                  >
                    Detect, prevent, and address technical issues or security
                    incidents;
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={(theme) => ({
                      color: (theme.vars || theme).palette.text.primary,
                      mb: 1,
                    })}
                  >
                    Comply with applicable laws, regulations, and legal process.
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography
                  variant="h3"
                  sx={(theme) => ({
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: (theme.vars || theme).palette.text.primary,
                    mb: 2,
                  })}
                >
                  4. Legal Bases for Processing (EU/UK visitors)
                </Typography>
                <Typography
                  variant="body1"
                  sx={(theme) => ({
                    color: (theme.vars || theme).palette.text.primary,
                    lineHeight: 1.6,
                    mb: 3,
                  })}
                >
                  If you are located in the European Economic Area, United
                  Kingdom, or Switzerland, we process your personal information
                  on one or more of the following bases: (a) your consent, (b)
                  performance of a contract, (c) our legitimate interests (e.g.,
                  Site security, product development), or (d) compliance with a
                  legal obligation.
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="h3"
                  sx={(theme) => ({
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: (theme.vars || theme).palette.text.primary,
                    mb: 2,
                  })}
                >
                  5. Sharing & Disclosure
                </Typography>
                <Typography
                  variant="body1"
                  sx={(theme) => ({
                    color: (theme.vars || theme).palette.text.primary,
                    lineHeight: 1.6,
                    mb: 2,
                  })}
                >
                  We do not sell or rent your personal information. We may share
                  it for:
                </Typography>
                <Box sx={{ ml: 2, mb: 3 }}>
                  <Typography
                    variant="body1"
                    sx={(theme) => ({
                      color: (theme.vars || theme).palette.text.primary,
                      mb: 2,
                    })}
                  >
                    <strong>Service Providers</strong> – With trusted vendors
                    who perform services for us (email delivery, analytics,
                    cloud hosting) and who are bound by confidentiality
                    obligations.
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={(theme) => ({
                      color: (theme.vars || theme).palette.text.primary,
                      mb: 2,
                    })}
                  >
                    <strong>Legal & Compliance</strong> – To comply with law,
                    court orders, or governmental requests; to enforce our
                    Terms; or to protect rights, property, or safety.
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={(theme) => ({
                      color: (theme.vars || theme).palette.text.primary,
                      mb: 1,
                    })}
                  >
                    <strong>Business Transfers</strong> – As part of any merger,
                    acquisition, financing, or sale of Company assets (we will
                    notify you of any such change).
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography
                  variant="h3"
                  sx={(theme) => ({
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: (theme.vars || theme).palette.text.primary,
                    mb: 2,
                  })}
                >
                  6. Retention
                </Typography>
                <Typography
                  variant="body1"
                  sx={(theme) => ({
                    color: (theme.vars || theme).palette.text.primary,
                    lineHeight: 1.6,
                    mb: 3,
                  })}
                >
                  Email addresses and related contact data are retained until
                  you withdraw consent or we determine the information is no
                  longer needed for the purposes described above, whichever
                  occurs first. Log and analytics data are typically retained no
                  longer than 24 months.
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="h3"
                  sx={(theme) => ({
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: (theme.vars || theme).palette.text.primary,
                    mb: 2,
                  })}
                >
                  7. Your Choices & Rights
                </Typography>
                <Box sx={{ ml: 2, mb: 3 }}>
                  <Typography
                    variant="body1"
                    sx={(theme) => ({
                      color: (theme.vars || theme).palette.text.primary,
                      mb: 2,
                    })}
                  >
                    <strong>Opt-Out of Marketing Emails</strong> – Click the
                    &quot;unsubscribe&quot; link in any marketing email or
                    contact us at privacy@bouncepoint.io.
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={(theme) => ({
                      color: (theme.vars || theme).palette.text.primary,
                      mb: 2,
                    })}
                  >
                    <strong>
                      Access, Correction, Deletion (where applicable)
                    </strong>{" "}
                    – Depending on your jurisdiction, you may have rights to
                    access, correct, delete, or restrict processing of your
                    personal information. To exercise these rights, email
                    info@bouncepoint.io.
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={(theme) => ({
                      color: (theme.vars || theme).palette.text.primary,
                      mb: 1,
                    })}
                  >
                    <strong>Do-Not-Track Signals</strong> – Our Site does not
                    currently respond to browser &quot;Do Not Track&quot;
                    signals.
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography
                  variant="h3"
                  sx={(theme) => ({
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: (theme.vars || theme).palette.text.primary,
                    mb: 2,
                  })}
                >
                  8. Security
                </Typography>
                <Typography
                  variant="body1"
                  sx={(theme) => ({
                    color: (theme.vars || theme).palette.text.primary,
                    lineHeight: 1.6,
                    mb: 3,
                  })}
                >
                  We use administrative, technical, and physical safeguards
                  designed to protect personal information. No online
                  transmission or storage system is completely secure;
                  therefore, we cannot guarantee absolute security.
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="h3"
                  sx={(theme) => ({
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: (theme.vars || theme).palette.text.primary,
                    mb: 2,
                  })}
                >
                  9. International Transfers
                </Typography>
                <Typography
                  variant="body1"
                  sx={(theme) => ({
                    color: (theme.vars || theme).palette.text.primary,
                    lineHeight: 1.6,
                    mb: 3,
                  })}
                >
                  Your information may be processed and stored in the United
                  States and other countries that may have privacy laws
                  different from those in your jurisdiction. We implement
                  appropriate safeguards for cross-border transfers as required
                  by applicable law.
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="h3"
                  sx={(theme) => ({
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: (theme.vars || theme).palette.text.primary,
                    mb: 2,
                  })}
                >
                  10. Children&apos;s Privacy
                </Typography>
                <Typography
                  variant="body1"
                  sx={(theme) => ({
                    color: (theme.vars || theme).palette.text.primary,
                    lineHeight: 1.6,
                    mb: 3,
                  })}
                >
                  The Site is not directed to children under 13, and we do not
                  knowingly collect personal information from them. If we learn
                  we have collected such information, we will delete it
                  promptly.
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="h3"
                  sx={(theme) => ({
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: (theme.vars || theme).palette.text.primary,
                    mb: 2,
                  })}
                >
                  11. Third-Party Links
                </Typography>
                <Typography
                  variant="body1"
                  sx={(theme) => ({
                    color: (theme.vars || theme).palette.text.primary,
                    lineHeight: 1.6,
                    mb: 3,
                  })}
                >
                  The Site may contain links to third-party websites or
                  services. We are not responsible for the privacy practices of
                  those third parties, and this Policy does not apply to them.
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="h3"
                  sx={(theme) => ({
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: (theme.vars || theme).palette.text.primary,
                    mb: 2,
                  })}
                >
                  12. Changes to This Policy
                </Typography>
                <Typography
                  variant="body1"
                  sx={(theme) => ({
                    color: (theme.vars || theme).palette.text.primary,
                    lineHeight: 1.6,
                    mb: 3,
                  })}
                >
                  We may update this Privacy Policy from time to time. We will
                  post the revised Policy with a new &quot;Last Updated&quot;
                  date and, if changes are material, provide additional notice.
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="h3"
                  sx={(theme) => ({
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    color: (theme.vars || theme).palette.text.primary,
                    mb: 2,
                  })}
                >
                  13. Governing Law & Venue
                </Typography>
                <Typography
                  variant="body1"
                  sx={(theme) => ({
                    color: (theme.vars || theme).palette.text.primary,
                    lineHeight: 1.6,
                    mb: 3,
                  })}
                >
                  This Privacy Policy is governed by and construed in accordance
                  with the laws of the State of Washington, without regard to
                  conflict-of-laws principles. Any dispute arising out of or
                  relating to this Policy shall be resolved exclusively in the
                  state or federal courts located in Tacoma, Washington, and the
                  parties hereby submit to the personal jurisdiction of such
                  courts.
                </Typography>
              </Box>
            </Stack>
          </PolicyPaper>
        </Container>
      </PolicySection>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;
